import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';

export type PersonIncomeSalaryValues = {
  salary: string;
  other_income: string;
  id: number;
};

@Component({
  selector: 'app-signup-unified-person-income',
  templateUrl: './signup-unified-person-income.component.html',
  styleUrls: ['./signup-unified-person-income.component.scss'],
})
export class SignupUnifiedPersonIncomeComponent implements OnInit, OnDestroy {
  form = this.formBuilder.group({
    wage: [null, [Validators.required, Validators.min(1)]],
    other_income: [0], // Opcional, sem validação de mínimo
  });

  event: any;
  data: PersonIncomeSalaryValues;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, unknown> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private toast: ToastService,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'income') {
        this.sendData();
      }
    });

    this.getData();

    this.form.statusChanges.subscribe((status) => {
      this.signupService.setPersonFormStatus(
        'incomeSalary',
        status === 'VALID'
      );
    });
    
    // Atualiza o status quando o valor do campo wage mudar
    this.form.controls.wage.valueChanges.subscribe(() => {
      this.signupService.setPersonFormStatus(
        'incomeSalary',
        this.form.valid
      );
    });
    
    // Atualiza o status inicial do formulário
    setTimeout(() => {
      this.signupService.setPersonFormStatus(
        'incomeSalary',
        this.form.valid
      );
    }, 0);
  }

  async getData() {
    try {
      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');
      
      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }
      
      const { data } = await this.api.get({
        route: 'api/registration/wage/',
        params: { register_uuid: routeId },
        token: true,
      });

      // Verifica se data existe antes de acessar suas propriedades
      if (data) {
        const formData = {
          wage: data.wage || data.salary || null,
          other_income: data.other_income || 0,
        };

        this.form.patchValue(formData);
        this.data = data;
        this.initialPayload = {
          wage: formData.wage,
          other_income: formData.other_income,
        };
      } else {
        this.data = null;
        this.initialPayload = null;
      }
      
      // Atualiza o status do formulário após carregar os dados
      setTimeout(() => {
        this.signupService.setPersonFormStatus(
          'incomeSalary',
          this.form.valid
        );
      }, 0);
    } catch (error) {
      this.initialPayload = null;
      // Em caso de erro, também atualiza o status
      setTimeout(() => {
        this.signupService.setPersonFormStatus(
          'incomeSalary',
          this.form.valid
        );
      }, 0);
    }
  }

  private buildPayloadFromForm(): Record<string, unknown> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      wage: valuesHelper.get('wage'),
      other_income: valuesHelper.get('other_income'),
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show('error', 'Erro', 'UUID do registro não encontrado na URL');
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload as Record<string, any>, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      await this.signupService.updateUserStepPF('register', 'declarations', routeId);
      return;
    }

    this.signupService.loading = true;

    try {
      if (this.data) {
        const { data } = await this.api.put({
          route: `api/registration/wage/${this.data.id}/`,
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });

        this.data = data;
      } else {
        await this.api.post({
          route: 'api/registration/wage/',
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      }

      this.initialPayload = { ...payload };
      await this.signupService.updateUserStepPF('register', 'declarations', routeId);
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      this.toast.show('error', 'Erro', error?.error?.message || 'Erro ao salvar os dados');
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }
}
